import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/feature/Sidebar';
import OfflineIndicator from '../../components/base/OfflineIndicator';
import { Toast } from '../../components/base/Toast';
import { api } from '../../services/api';

interface UserInfo { id: string; nom: string; role: string; }

const INITIAL_TYPES: string[] = [];
const INITIAL_FOURNISSEURS: string[] = [];

// Suggestions de prompts selon le type de tissu
const typePrompts: Record<string, string> = {
  Basin: 'basin riche African fabric close up shiny embossed texture warm tones Mali traditional textile elegant',
  Wax: 'wax print African ankara fabric close up vibrant geometric colorful pattern traditional west africa textile',
  Bogolan: 'bogolan mud cloth African fabric brown beige geometric traditional Mali artisan natural dyed textile',
  Pagne: 'pagne coton African fabric colorful pattern bright traditional west africa cotton textile close up',
  Soie: 'silk fabric smooth shiny imported luxury material close up neutral elegant background',
  Coton: 'cotton fabric African plain woven natural fiber texture close up neutral background Mali',
  Bazin: 'bazin riche African fabric shiny embossed texture traditional Mali elegant gold beige',
  Autre: 'African fabric textile close up traditional pattern Mali Bamako texture artisan',
};

function buildImageUrl(nom: string, type: string, couleur: string, seq: string): string {
  const typeDesc = typePrompts[type] || 'African fabric textile close up traditional pattern Mali';
  const colorDesc = couleur ? couleur + ' colored ' : '';
  const nameDesc = nom ? nom + ' ' : '';
  const prompt = nameDesc + colorDesc + typeDesc + ' high quality product photography simple clean background';
  return 'https://readdy.ai/api/search-image?query=%20%20%20encodeURIComponent%28prompt%29%20%20%20&width=400&height=400&seq=' + seq + '&orientation=squarish';
}

export default function StocksNouveau() {
  const navigate = useNavigate();
  const [user, setUser] = useState<UserInfo | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [saving, setSaving] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [imgSeq, setImgSeq] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [form, setForm] = useState({
    nom: '',
    type: '',
    customType: '',
    couleur: '',
    unite: 'mètres',
    quantiteStock: '',
    seuilAlerte: '',
    prixUnitaire: '',
    fournisseur: '',
    autreFournisseur: '',
    notes: '',
    imageUrl: '',
    imageMode: 'auto' as 'auto' | 'url',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const [typesList, setTypesList] = useState<string[]>(INITIAL_TYPES);
  const [fournisseursList, setFournisseursList] = useState<string[]>(INITIAL_FOURNISSEURS);

  useEffect(() => {
    const stored = localStorage.getItem('atelier_bassoum_user');
    const token = localStorage.getItem('atelier_bassoum_token');
    if (stored) setUser(JSON.parse(stored));
    else navigate('/login');

    if (token) {
      api.getStocks(token).then(stocks => {
        const tps = new Set(stocks.map((s: any) => s.type).filter(Boolean));
        if (tps.size > 0) setTypesList(Array.from(tps) as string[]);
      }).catch(() => {});

      api.getFournisseurs(token).then(f => {
        if (f && Array.isArray(f) && f.length > 0) {
          setFournisseursList(f.map((x: any) => x.nom));
        }
      }).catch(() => {});
    }
  }, [navigate]);

  const update = (key: string, value: string) => {
    setForm(prev => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors(prev => { const n = { ...prev }; delete n[key]; return n; });
  };

  // Génère une URL d'image basée sur les infos du produit
  const handleGenerateImage = () => {
    if (!form.nom && !form.type) {
      setToast({ message: 'Renseignez au moins le nom ou le type pour générer une image', type: 'error' });
      setTimeout(() => setToast(null), 3000);
      return;
    }
    const newSeq = 'newprod' + Date.now();
    setImgSeq(newSeq);
    setImageLoading(true);
    setImageError(false);
    const generatedUrl = buildImageUrl(form.nom, form.type, form.couleur, newSeq);
    setForm(prev => ({ ...prev, imageUrl: generatedUrl, imageMode: 'auto' }));
  };

  // Simule un upload de fichier → affiche preview local
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setToast({ message: 'Fichier invalide, choisissez une image (JPG, PNG…)', type: 'error' });
      setTimeout(() => setToast(null), 3000);
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const result = ev.target?.result as string;
      setForm(prev => ({ ...prev, imageUrl: result, imageMode: 'url' }));
      setImageError(false);
      setImageLoading(false);
    };
    reader.readAsDataURL(file);
  };

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (!form.nom.trim()) e.nom = 'Nom requis';
    if (!form.type) e.type = 'Type requis';
    if (form.type === 'Autre' && !form.customType.trim()) e.type = 'Précisez le type';
    // Removed couleur requirement to make it optional
    if (!form.quantiteStock || isNaN(Number(form.quantiteStock)) || Number(form.quantiteStock) < 0) e.quantiteStock = 'Quantité invalide';
    if (!form.seuilAlerte || isNaN(Number(form.seuilAlerte)) || Number(form.seuilAlerte) < 0) e.seuilAlerte = 'Seuil invalide';
    if (!form.prixUnitaire || isNaN(Number(form.prixUnitaire)) || Number(form.prixUnitaire) <= 0) e.prixUnitaire = 'Prix invalide';
    if (!form.fournisseur) e.fournisseur = 'Fournisseur requis';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;

    try {
        let finalFournisseur = form.fournisseur;
        if (form.fournisseur === 'Autre' && form.autreFournisseur.trim()) {
            finalFournisseur = form.autreFournisseur.trim();
            // Créer le fournisseur à la volée s'il n'existe pas
            await api.createFournisseur(token, { nom: finalFournisseur }).catch(() => {});
        }

        const stockData = {
          nom: form.nom,
          type: form.type === 'Autre' ? form.customType.trim() : form.type,
          couleur: form.couleur,
          quantite_stock: form.quantiteStock,
          unite: form.unite,
          seuil_alerte: form.seuilAlerte,
          prix_unitaire: form.prixUnitaire,
          fournisseur: finalFournisseur,
          image_url: form.imageUrl,
          notes: form.notes
        };
        await api.createStock(token, stockData);
        setToast({ message: `${form.nom} ajouté au stock avec succès !`, type: 'success' });
        setTimeout(() => navigate('/stocks'), 1500);
    } catch (err: any) {
        setToast({ message: err.message || "Erreur lors de la création du produit.", type: 'error' });
    } finally {
        setSaving(false);
    }
  };

  if (!user) return null;

  const inputClass = (field: string) =>
    `w-full border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-foret-400 ${
      errors[field] ? 'border-red-400 bg-red-50' : 'border-gray-200'
    }`;

  const hasImage = !!form.imageUrl && !imageError;
  const canGenerate = !!(form.nom || form.type);

  return (
    <div className="flex h-screen bg-gray-50 font-nunito overflow-hidden">
      <OfflineIndicator />
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <div className="hidden md:block">
        <Sidebar role={user.role} nom={user.nom} collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(v => !v)} />
      </div>

      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Header */}
        <header className="bg-white border-b border-gray-100 px-6 py-4 flex items-center gap-4 flex-shrink-0">
          <button
            onClick={() => navigate('/stocks')}
            className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-gray-100 cursor-pointer transition-colors text-gray-500"
            title="Retour à la liste des stocks"
          >
            <i className="ri-arrow-left-line text-lg" />
          </button>
          <div>
            <h2 className="font-playfair font-bold text-xl text-gray-900">Nouveau produit</h2>
            <p className="text-xs text-gray-400 mt-0.5">Ajouter un tissu ou article au stock</p>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-2xl mx-auto">

            {/* Preview card — mise à jour avec l'image */}
            <div className="bg-gradient-to-r from-foret-600 to-foret-500 rounded-2xl p-5 mb-6 text-white flex items-center gap-4">
              <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 bg-white/20 flex items-center justify-center">
                {hasImage ? (
                  <img
                    src={form.imageUrl}
                    alt="aperçu"
                    className="w-full h-full object-cover object-top"
                    onError={() => setImageError(true)}
                  />
                ) : (
                  <i className="ri-image-add-line text-2xl text-white/60" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-playfair font-bold text-xl truncate">{form.nom || 'Nom du produit'}</p>
                <p className="text-foret-200 text-sm mt-0.5">
                  {form.type || 'Type'} · {form.couleur || 'Couleur'}
                </p>
                <p className="text-white/70 text-xs mt-1">
                  {form.quantiteStock ? `${form.quantiteStock} ${form.unite}` : '—'} · {form.prixUnitaire ? `${Number(form.prixUnitaire).toLocaleString('fr-FR')} FCFA/${form.unite}` : '— FCFA'}
                </p>
              </div>
            </div>

            {/* Form */}
            <div className="bg-white rounded-2xl border border-gray-100 divide-y divide-gray-50">

              {/* ── Section IMAGE (nouvelle) ── */}
              <div className="p-6">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <div className="w-6 h-6 flex items-center justify-center rounded-full bg-terre-100 text-terre-700 text-xs font-bold">
                    <i className="ri-image-line text-xs" />
                  </div>
                  Illustration du tissu
                </h3>

                <div className="flex gap-4 items-start">
                  {/* Image preview box */}
                  <div
                    className="relative w-32 h-32 rounded-xl border-2 border-dashed border-gray-200 flex-shrink-0 overflow-hidden bg-gray-50 cursor-pointer group hover:border-foret-400 transition-colors"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {imageLoading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-10">
                        <div className="w-6 h-6 border-2 border-foret-400 border-t-transparent rounded-full animate-spin" />
                      </div>
                    )}
                    {hasImage ? (
                      <>
                        <img
                          src={form.imageUrl}
                          alt="aperçu tissu"
                          className="w-full h-full object-cover object-top"
                          onLoad={() => setImageLoading(false)}
                          onError={() => { setImageError(true); setImageLoading(false); }}
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center">
                          <span className="text-white text-xs font-semibold opacity-0 group-hover:opacity-100 transition-opacity text-center px-2">
                            Changer l&apos;image
                          </span>
                        </div>
                      </>
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-gray-300 group-hover:text-foret-400 transition-colors">
                        <div className="w-8 h-8 flex items-center justify-center">
                          <i className="ri-image-add-line text-2xl" />
                        </div>
                        <span className="text-xs text-center px-2 font-medium">Cliquez pour importer</span>
                      </div>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleFileChange}
                      title="Choisir une image de tissu"
                    />
                  </div>

                  {/* Actions & options */}
                  <div className="flex-1 space-y-3">
                    {/* Upload file */}
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-dashed border-gray-200 hover:border-foret-400 hover:bg-foret-50 cursor-pointer transition-all text-left group"
                    >
                      <div className="w-9 h-9 rounded-xl bg-foret-100 flex items-center justify-center flex-shrink-0 group-hover:bg-foret-200 transition-colors">
                        <i className="ri-upload-cloud-2-line text-foret-600 text-lg" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-800">Importer une photo</p>
                        <p className="text-xs text-gray-400">JPG, PNG, WEBP — depuis votre appareil</p>
                      </div>
                    </button>

                    {/* Auto-generate */}
                    <button
                      onClick={handleGenerateImage}
                      disabled={!canGenerate}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 cursor-pointer transition-all text-left group ${
                        canGenerate
                          ? 'border-or-300 hover:border-or-400 hover:bg-or-50'
                          : 'border-gray-100 opacity-50 cursor-not-allowed'
                      }`}
                    >
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${canGenerate ? 'bg-or-100 group-hover:bg-or-200' : 'bg-gray-100'}`}>
                        <i className={`ri-magic-line text-lg ${canGenerate ? 'text-or-600' : 'text-gray-400'}`} />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-800">
                          {hasImage ? 'Regénérer l\'illustration' : 'Générer automatiquement'}
                        </p>
                        <p className="text-xs text-gray-400">
                          {canGenerate ? 'Basée sur le nom, type et couleur renseignés' : 'Renseignez d\'abord le nom ou le type'}
                        </p>
                      </div>
                      {hasImage && (
                        <div className="ml-auto flex-shrink-0">
                          <span className="text-xs bg-or-100 text-or-700 px-2 py-0.5 rounded-full font-medium whitespace-nowrap">Regénérer</span>
                        </div>
                      )}
                    </button>

                    {/* URL manuelle */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5 flex items-center gap-1">
                        <i className="ri-link text-gray-400" />
                        Ou coller une URL d&apos;image
                      </label>
                      <div className="relative">
                        <input
                          type="url"
                          value={form.imageMode === 'url' && !form.imageUrl.startsWith('data:') ? form.imageUrl : ''}
                          onChange={e => {
                            const v = e.target.value;
                            setForm(prev => ({ ...prev, imageUrl: v, imageMode: 'url' }));
                            setImageError(false);
                            setImageLoading(false);
                          }}
                          placeholder="https://exemple.com/tissu.jpg"
                          className="w-full border border-gray-200 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-foret-400 pr-10"
                        />
                        {form.imageUrl && (
                          <button
                            onClick={() => { setForm(prev => ({ ...prev, imageUrl: '', imageMode: 'auto' })); setImageError(false); }}
                            className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-red-500 cursor-pointer"
                            title="Effacer l'URL de l'image"
                          >
                            <i className="ri-close-line text-sm" />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Status */}
                    {hasImage && (
                      <div className="flex items-center gap-2 text-xs text-foret-700 bg-foret-50 rounded-lg px-3 py-2">
                        <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                          <i className="ri-checkbox-circle-line text-foret-500" />
                        </div>
                        Image prête — visible dans la fiche produit et le stock
                      </div>
                    )}
                    {imageError && (
                      <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">
                        <i className="ri-alert-line" />
                        Impossible de charger cette image. Vérifiez l&apos;URL ou importez un fichier.
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Section 1: Identification */}
              <div className="p-6">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <div className="w-6 h-6 flex items-center justify-center rounded-full bg-foret-100 text-foret-700 text-xs font-bold">1</div>
                  Identification du produit
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Nom du produit *</label>
                    <input
                      type="text"
                      value={form.nom}
                      onChange={e => update('nom', e.target.value)}
                      placeholder="Ex : Basin Riche Doré, Wax Bleu Fleur…"
                      className={inputClass('nom')}
                    />
                    {errors.nom && <p className="text-xs text-red-500 mt-1">{errors.nom}</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5 flex justify-between">
                      Type de tissu *
                      {form.type && form.type !== 'Autre' && (
                          <button type="button" onClick={() => setTypesList(typesList.filter(t => t !== form.type))} className="text-red-400 hover:text-red-600 text-[10px] font-normal flex items-center">
                              <i className="ri-delete-bin-line mr-1"></i> Retirer l'option
                          </button>
                      )}
                    </label>
                    <select 
                      value={form.type} 
                      onChange={e => update('type', e.target.value)} 
                      className={inputClass('type')}
                      title="Sélectionner le type de tissu"
                    >
                      <option value="">Sélectionner…</option>
                      {typesList.map(t => <option key={t} value={t}>{t}</option>)}
                      <option value="Autre">Autre (Nouveau type...)</option>
                    </select>
                    {form.type === 'Autre' && (
                      <input
                        type="text"
                        value={form.customType}
                        onChange={e => update('customType', e.target.value)}
                        placeholder="Précisez le type"
                        className={`mt-2 ${inputClass('type')}`}
                      />
                    )}
                    {errors.type && <p className="text-xs text-red-500 mt-1">{errors.type}</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Couleur / Motif</label>
                    <input
                      type="text"
                      value={form.couleur}
                      onChange={e => update('couleur', e.target.value)}
                      placeholder="Ex : Or/Beige, Bleu/Blanc…"
                      className={inputClass('couleur')}
                    />
                    {errors.couleur && <p className="text-xs text-red-500 mt-1">{errors.couleur}</p>}
                  </div>
                </div>
              </div>

              {/* Section 2: Stock */}
              <div className="p-6">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <div className="w-6 h-6 flex items-center justify-center rounded-full bg-or-100 text-or-700 text-xs font-bold">2</div>
                  Stock & Prix
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Unité</label>
                    <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
                      {['mètres', 'pièces', 'yards'].map(u => (
                        <button
                          key={u}
                          onClick={() => update('unite', u)}
                          className={`flex-1 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-colors whitespace-nowrap ${
                            form.unite === u ? 'bg-white text-foret-700' : 'text-gray-500'
                          }`}
                        >
                          {u}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Quantité initiale *</label>
                    <input
                      type="number"
                      value={form.quantiteStock}
                      onChange={e => update('quantiteStock', e.target.value)}
                      min="0"
                      step="0.5"
                      placeholder="0"
                      className={inputClass('quantiteStock')}
                    />
                    {errors.quantiteStock && <p className="text-xs text-red-500 mt-1">{errors.quantiteStock}</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Seuil d&apos;alerte *</label>
                    <input
                      type="number"
                      value={form.seuilAlerte}
                      onChange={e => update('seuilAlerte', e.target.value)}
                      min="0"
                      placeholder="Ex : 10"
                      className={inputClass('seuilAlerte')}
                    />
                    {errors.seuilAlerte && <p className="text-xs text-red-500 mt-1">{errors.seuilAlerte}</p>}
                  </div>

                  <div className="sm:col-span-3">
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                      Prix unitaire (FCFA / {form.unite}) *
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        value={form.prixUnitaire}
                        onChange={e => update('prixUnitaire', e.target.value)}
                        min="0"
                        placeholder="Ex : 4500"
                        className={`${inputClass('prixUnitaire')} pr-16`}
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-400">FCFA</span>
                    </div>
                    {errors.prixUnitaire && <p className="text-xs text-red-500 mt-1">{errors.prixUnitaire}</p>}
                    {form.quantiteStock && form.prixUnitaire && (
                      <p className="text-xs text-foret-700 font-semibold mt-1.5 flex items-center gap-1">
                        <i className="ri-price-tag-3-line" />
                        Valeur totale : {(Number(form.quantiteStock) * Number(form.prixUnitaire)).toLocaleString('fr-FR')} FCFA
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Section 3: Fournisseur */}
              <div className="p-6">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <div className="w-6 h-6 flex items-center justify-center rounded-full bg-terre-100 text-terre-700 text-xs font-bold">3</div>
                  Fournisseur & Notes
                </h3>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5 flex justify-between">
                        Fournisseur *
                        {form.fournisseur && form.fournisseur !== 'Autre' && (
                          <button type="button" onClick={() => setFournisseursList(fournisseursList.filter(f => f !== form.fournisseur))} className="text-red-400 hover:text-red-600 text-[10px] font-normal flex items-center">
                              <i className="ri-delete-bin-line mr-1"></i> Retirer
                          </button>
                      )}
                    </label>
                    <select 
                      value={form.fournisseur} 
                      onChange={e => update('fournisseur', e.target.value)} 
                      className={inputClass('fournisseur')}
                      title="Sélectionner un fournisseur"
                    >
                      <option value="">Sélectionner un fournisseur…</option>
                      {fournisseursList.map(f => <option key={f} value={f}>{f}</option>)}
                      <option value="Autre">Autre (Nouveau fournisseur...)</option>
                    </select>
                    {errors.fournisseur && <p className="text-xs text-red-500 mt-1">{errors.fournisseur}</p>}
                  </div>

                  {form.fournisseur === 'Autre' && (
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">Nom du fournisseur</label>
                      <input
                        type="text"
                        value={form.autreFournisseur}
                        onChange={e => update('autreFournisseur', e.target.value)}
                        placeholder="Entrez le nom du fournisseur"
                        className={inputClass('autreFournisseur')}
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Notes internes</label>
                    <textarea
                      value={form.notes}
                      onChange={e => update('notes', e.target.value)}
                      rows={3}
                      maxLength={500}
                      placeholder="Informations supplémentaires, origine, spécificités…"
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-foret-400 resize-none"
                    />
                    <p className="text-xs text-gray-400 text-right mt-0.5">{form.notes.length}/500</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Alert stock */}
            {form.quantiteStock && form.seuilAlerte && Number(form.quantiteStock) <= Number(form.seuilAlerte) && (
              <div className="mt-4 bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center text-red-500 flex-shrink-0">
                  <i className="ri-alert-line" />
                </div>
                <p className="text-sm text-red-700">
                  <strong>Attention :</strong> La quantité initiale est inférieure ou égale au seuil d&apos;alerte — le produit sera signalé en alerte dès la création.
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => navigate('/stocks')}
                className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-600 font-semibold cursor-pointer hover:bg-gray-50 whitespace-nowrap"
              >
                Annuler
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 py-3 rounded-xl bg-foret-500 hover:bg-foret-600 text-white font-semibold cursor-pointer transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap flex items-center justify-center gap-2"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Enregistrement…
                  </>
                ) : (
                  <>
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-save-line" />
                    </div>
                    Enregistrer le produit
                  </>
                )}
              </button>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
